import { useNavigate, useParams } from 'react-router-dom';
import { IconButton, Tooltip, Stack } from '@mui/material';
import { AddComment, Txt } from '@carbon/icons-react';
import { GridRenderCellParams } from '@mui/x-data-grid';

import { useLocalization } from 'localization';
import { IUserBranchDto } from 'api/users/dtos';
import EyesIcon from 'components/v2/icons/EyesIcon';
import routes from 'app.routes';
import { IDatahubOrderLine, ISimplifiedOrder } from 'api';
import StatusChip from 'components/v2/chip/StatusChip';
import { CurrencyCode } from 'constants/enums/general';
import { MODAL_ID, MODALS_STORE } from 'constants/ui/modal';

type Props = {
  userByBranchId?: IUserBranchDto[];
};

export const ordersInCaseColumn = (props: Props) => {
  const { userByBranchId } = props;
  const { id } = useParams();
  const { t, f } = useLocalization();
  const navigate = useNavigate();

  return [
    {
      headerName: t('orders.title.orderNumber'),
      field: 'salesId',
      width: 200,
      sortable: false,
    },
    {
      headerName: t('common.terms.creationDate'),
      field: 'createdDate',
      width: 200,
      sortable: false,
      valueGetter: (value: ISimplifiedOrder['createdDate']) => {
        return value ? f(new Date(value), { date: 'short' }) : '-';
      },
    },
    {
      headerName: t('common.terms.responsibleAgent'),
      field: 'responsibleUserId',
      width: 200,
      flex: 1,
      sortable: false,

      renderCell: (params: GridRenderCellParams<ISimplifiedOrder, ISimplifiedOrder['responsibleUserId']>) => {
        const value = params.value;
        const foundUser = userByBranchId?.find(user => user.contactPersonId === value);
        const displayedName = foundUser ? `${foundUser.firstName} ${foundUser.lastName}` : '-';
        return <Stack justifyContent="center">{displayedName}</Stack>;
      },
    },
    {
      headerName: t('common.terms.status'),
      field: 'status',
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ISimplifiedOrder, ISimplifiedOrder['status']>) => {
        const rawStatus = params.value;
        return <StatusChip status={rawStatus ?? ''} />;
      },
    },
    {
      headerName: t('common.terms.actions'),
      field: 'id',
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ISimplifiedOrder, ISimplifiedOrder['id']>) => {
        const value = params.value;
        return (
          <Tooltip title={t('common.terms.viewDetails')}>
            <IconButton
              aria-label="view-order-in-case"
              onClick={() => navigate(`${routes.v2.cases.overview}/${id}/orders/${value}`)}
              sx={{ border: '1px solid' }}
            >
              <EyesIcon />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];
};

export const orderLinesInCaseColumn = () => {
  const { t, f } = useLocalization();
  const { id, orderId } = useParams();
  const { toggleModal } = MODALS_STORE.orders();

  return [
    {
      headerName: t('common.terms.product'),
      field: 'productName',
      width: 250,
      flex: 1,
      minWidth: 200,
      sortable: false,
    },
    {
      headerName: t('common.terms.quantity'),
      field: 'orderedQuantity',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams<IDatahubOrderLine, IDatahubOrderLine['orderedQuantity']>) => {
        const value = params.value;
        return (
          <>
            {f(value ?? 0, { decimals: 0 })} {params?.row?.unit}
          </>
        );
      },
    },
    {
      headerName: t('common.terms.deliveredQuantity'),
      field: 'deliveredQuantity',
      width: 170,
      sortable: false,
      renderCell: (params: GridRenderCellParams<IDatahubOrderLine, IDatahubOrderLine['deliveredQuantity']>) => {
        const value = params.value;
        return (
          <>
            {f(value ?? 0, { decimals: 0 })} {params?.row?.unit}
          </>
        );
      },
    },
    {
      headerName: t('common.terms.deliveredDate'),
      field: 'deliveredAt',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams<IDatahubOrderLine, IDatahubOrderLine['deliveredAt']>) => {
        const value = params.value;
        return <>{value ? f(value, { date: 'short' }) : '-'}</>;
      },
    },
    {
      headerName: t('common.terms.status'),
      field: 'status',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams<IDatahubOrderLine, IDatahubOrderLine['status']>) => {
        const value = params.value;
        return <StatusChip status={value ?? ''} />;
      },
    },
    {
      headerName: t('common.terms.price'),
      field: 'price',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams<IDatahubOrderLine, IDatahubOrderLine['price']>) => {
        const value = params.value;
        return <>{f(value ?? 0.0, { currency: CurrencyCode.DKK, decimals: 2 })}</>;
      },
    },
    {
      headerName: t('common.terms.actions'),
      field: 'canReceiveFeedback',
      width: 100,
      sortable: false,
      align: 'center',
      renderCell: (params: GridRenderCellParams<IDatahubOrderLine, IDatahubOrderLine['canReceiveFeedback']>) => {
        const value = params.value;
        const row = params.row;
        const notes = row.notes;
        return (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" height="100%">
            <Tooltip title={t('orders.pages.details.giveFeedbackAction')}>
              {/* TODO: Implement new FeedbackPageContent*/}
              <IconButton
                aria-label="view-order-in-case"
                sx={{ border: '1px solid' }}
                disabled={!value}
                // href={routes.feedback({
                //   caseId: id ?? '',
                //   orderId: orderId ?? '',
                //   orderlineId: row.id,
                //   vendorOrderLineId: row.vendorOrderLineId,
                // })}
                onClick={() =>
                  toggleModal(MODAL_ID.ADD_FEEDBACK_ON_ORDER_LINE, {
                    caseId: id ?? '',
                    orderId: orderId ?? '',
                    orderLineId: row.id,
                    vendorOrderLineId: row.vendorOrderLineId,
                  })
                }
              >
                <AddComment size={20} />
              </IconButton>
            </Tooltip>
            {notes && (
              <Tooltip title={t('orders.pages.details.textTaskNotes')}>
                <IconButton
                  aria-label="view-text-task-notes"
                  sx={{ border: '1px solid' }}
                  onClick={() => toggleModal(MODAL_ID.TEXT_TASK_NOTES, { notes })}
                >
                  <Txt size={20} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];
};
export const contactPersonsColumn = () => {
  const { t } = useLocalization();
  return [
    {
      headerName: t('common.terms.name'),
      field: 'name',
      width: 300,
      sortable: false,
    },
    {
      headerName: t('common.terms.type'),
      field: 'type',
      width: 200,
      sortable: false,
    },
    {
      headerName: t('common.terms.phone'),
      field: 'phone',
      width: 250,
      sortable: false,
    },
    {
      headerName: t('common.terms.phoneAlt'),
      field: 'phoneAlt',
      width: 250,
      sortable: false,
    },
    {
      headerName: t('common.terms.email'),
      field: 'email',
      width: 300,
      sortable: false,
      flex: 1,
    },
  ];
};
